import { createStore, combineReducers, applyMiddleware, Store } from 'redux';
import { useSelector as useReduxSelector, TypedUseSelectorHook } from 'react-redux';
import { thunk } from 'redux-thunk';
import { persistStore, persistReducer, Persistor, PersistConfig } from 'redux-persist';
import { composeWithDevTools } from 'redux-devtools-extension';
import localforage from 'localforage';

import * as BorrowerStore from './borrower';
import * as PropertiesStore from './properties';
import * as FormsStore from './forms';
import * as UserStore from './user';

const rootReducer = combineReducers({
    borrower: BorrowerStore.borrowerReducer,
    properties: PropertiesStore.propertiesReducer,
    forms: FormsStore.formCollectionReducer,
    user: UserStore.userReducer,
});

const middlewares = [thunk];
let enhancers = applyMiddleware(...middlewares);

/* istanbul ignore if */
if (process.env.NODE_ENV === 'development') {
    enhancers = composeWithDevTools(enhancers);
}

type ApplyRootStateType = ReturnType<typeof rootReducer>;

const initialState: ApplyRootStateType = {
    borrower: { ...BorrowerStore.Types.borrowerInitialState },
    properties: { ...PropertiesStore.Types.propertiesInitialState },
    forms: { ...FormsStore.Types.formCollectionInitialState },
    user: { ...UserStore.Types.userInitialState },
};

export const configureStore = (): [Store<ApplyRootStateType>, Persistor] => {
    const rootPersistConfig: PersistConfig<ApplyRootStateType> = {
        key: 'apply-root',
        storage: localforage,
        blacklist: ['properties', 'borrower', 'forms', 'user'],
        timeout: 0,
        throttle: 100,
        debug: process.env.NODE_ENV === 'development',
    };

    const persistedReducer = persistReducer<ApplyRootStateType>(rootPersistConfig, rootReducer);

    const store = createStore(persistedReducer, initialState, enhancers);

    const persistor = persistStore(store, undefined, () => {
        // use this callback for actions that need to happen after rehydration
    });

    return [store, persistor];
};

const [store, persistor] = configureStore();

export const useApplySelector: TypedUseSelectorHook<ApplyRootStateType> = useReduxSelector;

export { store as ApplyStore, persistor as ApplyPersistor, ApplyRootStateType };
